import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { format, addDays, startOfWeek } from 'date-fns';
import AddAgendaModal from '../myEventDashboard/agendaTab/AddAgendaModal';
import { useEventOperationsData } from '../../contexts/EventContext';
import {
  findNearestSlotIdxAfter,
  findNearestSlotIdxBefore,
  computeSlotIndices,
  assignColumns,
  TIME_REGEX
} from '../../utils/timeslotCalculationLogic';

// Helper to generate 30-min time slots from 8:00 to 8:00 (24 hours)
const generateTimeSlots = () => {
  const slots = [];
  const baseDate = new Date();
  baseDate.setHours(8, 0, 0, 0);
  for (let i = 0; i < 48; i++) {
    slots.push(format(baseDate, 'HH:mm'));
    baseDate.setMinutes(baseDate.getMinutes() + 30);
  }
  return slots;
};

// Helper to get the slot index for a given time string ("HH:mm:ss+00")
const getSlotIndex = (timeStr, timeSlots) => {
  if (!timeStr) return -1;
  // Accepts "HH:mm:ss+00" or "HH:mm:ss"
  const match = timeStr.match(/(\d{2}):(\d{2})/);
  if (!match) return -1;
  const hhmm = `${match[1]}:${match[2]}`;
  return timeSlots.indexOf(hhmm);
};

// Helper to normalize time string to HH:mm format
const normalizeTimeString = (timeStr) => {
  if (!timeStr) return '';
  const match = timeStr.match(/(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : '';
};

// Normalize agenda item for AddAgendaModal
function normalizeAgendaItem(item) {
  if (!item) return {};
  return {
    ...item,
    name: item.name || '',
    description: item.description || '',
    startTime: normalizeTimeString(item.startTime || item.start_time || ''),
    endTime: normalizeTimeString(item.endTime || item.end_time || ''),
    assignmentGroupId: item.assignmentGroupId || item.assignment_group_id || '',
    date: item.date || '',
    spaceId: item.spaceId || item.space_id || '',
    agendaId: item.agendaId || item.agenda_id || item.id || '',
  };
}

const WeekView = ({ date, items = [], timeSlots = [], onAgendaUpdated, eventId }) => {
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAgenda, setEditingAgenda] = useState(null);

  // Convert dayjs date to JS Date if needed
  const currentDate = date && date.$d ? date.$d : date;
  const weekStart = startOfWeek(currentDate);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const slots = timeSlots.length ? timeSlots : generateTimeSlots();

  // Group agenda items by day and slot
  // Structure: { [dateKey]: { [slotIdx]: [agendaItem, ...] } }
  const itemsByDaySlot = {};
  items.forEach(item => {
    const dayKey = item.date; // 'YYYY-MM-DD'
    const startIdx = getSlotIndex(item.startTime || item.start_time, slots);
    if (startIdx === -1) return;
    if (!itemsByDaySlot[dayKey]) itemsByDaySlot[dayKey] = {};
    if (!itemsByDaySlot[dayKey][startIdx]) itemsByDaySlot[dayKey][startIdx] = [];
    itemsByDaySlot[dayKey][startIdx].push(item);
  });

  // Handle agenda item click
  const handleAgendaClick = (item) => {
    setEditingAgenda(item);
    setModalOpen(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingAgenda(null);
  };

  // Handle agenda save (refresh items if parent provides onAgendaUpdated)
  const handleAgendaSave = (updatedItem) => {
    setModalOpen(false);
    setEditingAgenda(null);
    if (onAgendaUpdated) onAgendaUpdated(updatedItem);
  };

  const { tasks = [] } = useEventOperationsData();

  return (
    <Box sx={{ overflowX: 'auto', width: '100%', height: '100%' }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '120px repeat(7, 1fr)',
        borderRadius: 2,
        background: '#fff',
        minHeight: '100%'
      }}>
        {/* Header Row */}
        <Box sx={{
          p: 1,
          background: '#fff',
          borderBottom: '1px solid #e3e6f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Typography variant="subtitle2" sx={{ color: '#666' }}>Time</Typography>
        </Box>
        {weekDays.map((day, index) => (
          <Box
            key={index}
            sx={{
              p: 1,
              background: '#fff',
              borderBottom: '1px solid #e3e6f0',
              textAlign: 'center',
            }}
          >
            <Typography variant="subtitle2" sx={{ color: '#3730a3', fontWeight: 600 }}>
              {format(day, 'EEE')}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>
              {format(day, 'MMM d')}
            </Typography>
          </Box>
        ))}

        {/* Time slots as rows */}
        {slots.map((slot, rowIdx) => (
          <React.Fragment key={slot}>
            {/* Time label */}
            <Box sx={{
              p: 1,
              background: '#fff',
              borderBottom: '1px solid #e3e6f0',
              color: '#888',
              fontWeight: 500,
              fontSize: 14,
              textAlign: 'center',
            }}>
              {slot}
            </Box>
            {/* Cells for each day */}
            {weekDays.map((day, dayIdx) => {
              const dateKey = format(day, 'yyyy-MM-dd');
              // All items for this day
              const dayItems = items.filter(item => item.date === dateKey);
              // Precompute slot indices for all items
              const itemsWithSlots = dayItems.map(ev => computeSlotIndices(ev, slots));
              // Sort by startIdx, then id
              const { itemsWithColumns, maxColumns } = assignColumns(itemsWithSlots);
              // Only render items that start at this slot
              const itemsStartingHere = itemsWithColumns.filter(item => item._startIdx === rowIdx);
              return (
                <Box
                  key={dayIdx}
                  sx={{
                    borderBottom: '1px solid #e3e6f0',
                    borderLeft: dayIdx === 0 ? '1px solid #e3e6f0' : 'none',
                    height: 48,
                    background: '#fff',
                    position: 'relative',
                  }}
                >
                  {itemsStartingHere.map(item => {
                    // Calculate true pixel height based on minutes
                    const startMatch = (item.startTime || item.start_time).match(TIME_REGEX);
                    const endMatch = (item.endTime || item.end_time).match(TIME_REGEX);
                    const startMinutes = startMatch ? parseInt(startMatch[1]) * 60 + parseInt(startMatch[2]) : 0;
                    const endMinutes = endMatch ? parseInt(endMatch[1]) * 60 + parseInt(endMatch[2]) : 0;
                    const slotStartMinutes = slots[item._startIdx] ? (parseInt(slots[item._startIdx].split(':')[0]) * 60 + parseInt(slots[item._startIdx].split(':')[1])) : 0;
                    const slotEndMinutes = slots[item._endIdx] ? (parseInt(slots[item._endIdx].split(':')[0]) * 60 + parseInt(slots[item._endIdx].split(':')[1])) : 0;
                    const topOffset = Math.max(0, startMinutes - slotStartMinutes);
                    const bottomOffset = Math.max(0, (slotEndMinutes + 30) - endMinutes);
                    const totalMinutes = endMinutes - startMinutes;
                    let heightPx = 0;
                    if (item._startIdx === item._endIdx) {
                      heightPx = (totalMinutes / 30) * 48;
                    } else {
                      heightPx = (item._endIdx - item._startIdx + 1) * 48;
                      heightPx -= (topOffset / 30) * 48;
                      heightPx -= (bottomOffset / 30) * 48;
                    }
                    heightPx = Math.max(heightPx, 18);
                    const colIndex = item._column || 0;
                    const width = `calc(${100 / maxColumns}% - 4px)`;
                    const left = `calc(${(100 / maxColumns) * colIndex}% + 2px)`;
                    return (
                      <Box
                        key={item.agendaId || item.id}
                        sx={{
                          position: 'absolute',
                          left,
                          width,
                          top: 0,
                          height: `${heightPx}px`,
                          zIndex: 2,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: '#e0e7ff',
                          color: '#3730a3',
                          border: '1.5px solid #a5b4fc',
                          boxShadow: '0 2px 8px 0 rgba(0,0,0,0.04)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          gap: 0.5,
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          overflow: 'hidden',
                        }}
                        onClick={() => handleAgendaClick(item)}
                      >
                        <Typography variant="subtitle2" sx={{ 
                          fontWeight: 700, 
                          color: '#6d7eea', 
                          mb: 0.5,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          width: '100%',
                          maxWidth: '100%',
                          lineHeight: 1.2
                        }}>
                          {item.name}
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          color: '#222',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          width: '100%',
                          maxWidth: '100%',
                          lineHeight: 1.2
                        }}>
                          {item.space?.name}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
      {/* AddAgendaModal for editing/viewing agenda items */}
      {modalOpen && editingAgenda && (
        <AddAgendaModal
          open={modalOpen}
          onClose={handleCloseModal}
          onSave={handleAgendaSave}
          initialData={normalizeAgendaItem(editingAgenda)}
          isEdit={!!editingAgenda}
          eventId={eventId}
          tasks={tasks}
        />
      )}
    </Box>
  );
};

export default WeekView; 